import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Phone, ScanLine, Search, ShieldAlert, UserPlus, Users } from 'lucide-react';
import { formatCurrency, formatDuration } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { apiMessage, statusOf } from '../lib/helpers';
import { collectionState, releaseGates } from '../kidsDesk';
import { useVisitPicker } from '../lib/visits';
import { meterFor, type ResourceRow, type SessionRow } from '../lib/sessions';
import { PricingRuleList } from '@/Pages/Pricing/components/PricingRuleList';
import { RefusalNotice } from '@/Pages/Pricing/components/RefusalNotice';
import { useRefusalToast } from '@/Pages/Pricing/lib/useRefusal';
import { pricingListRoute } from '@/Pages/Pricing/pricingRoutes';
import {
  isPricingRefusal,
  refusalOf,
  type Refusal as PricingRefusal,
} from '@/Pages/Pricing/lib/refusals';
import { ResourceFormDialog } from './ResourceFormDialog';
import { ChildGuardians } from './ChildGuardians';
import { GuardianFields } from './GuardianFields';
import { VisitPicker } from './VisitPicker';
import { KidsWaitingList } from './KidsWaitingList';
import {
  emptyGuardian,
  guardianBody,
  guardianIdsOf,
  guardianIsComplete,
  searchChildren,
  unambiguousGuardianId,
  useChildDirectory,
  useKidsOccupancy,
  useWaitingList,
  type ChildRow,
  type GuardianDraft,
  type GuardianRow,
  type KidsCheckInResult,
} from './kids-area';

interface KidsDialogProps {
  open: boolean;
  resource: ResourceRow | null;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}

/**
 * §11 — check a child into a kids area.
 *
 * This deliberately does not go through the generic `/resource-sessions` start.
 * That endpoint takes a table and a child id and nothing else, so a session
 * started through it records no guardian and issues no wristband — which are the
 * two facts the desk exists to hold. `/kids-area/check-in` refuses a child with
 * no guardian on file, so somebody to call is captured here or the check-in
 * cannot happen.
 *
 * The dialog is a **lookup first and a registration second**, which is the whole
 * reason a `Child` is now a persistent record: a family that comes every
 * Saturday is found by the band on the wrist, by the child's name, or by the
 * number of whoever is standing at the desk — and nothing is retyped. Only a
 * child the venue has never seen goes down the second path.
 */
export function KidsCheckInDialog({
  open,
  resource,
  currency,
  onClose,
  onDone,
}: KidsDialogProps) {
  const { t } = useTranslation();
  const { can } = usePermission();

  const [mode, setMode] = useState<'find' | 'new'>('find');
  const [query, setQuery] = useState({ name: '', tagCode: '', guardianPhone: '' });
  const [results, setResults] = useState<ChildRow[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [child, setChild] = useState<ChildRow | null>(null);

  const [childName, setChildName] = useState('');
  const [newTagCode, setNewTagCode] = useState('');
  const [guardians, setGuardians] = useState<GuardianDraft[]>([{ ...emptyGuardian, isPrimary: true }]);

  const [plannedMinutes, setPlannedMinutes] = useState('60');
  const [pricingRuleId, setPricingRuleId] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * The refusal this whole change was reported over.
   *
   * "Kids #01 has no pricing rule... Add one at POST /resources/cmt64plmr…/
   * pricing-rules" is what an attendant was shown here, in English, holding a
   * child. It is a banner with a way out now, in the venue's own words, exactly
   * as it is on the console start dialog — a check-in opens a timed session, so
   * it meets the same three refusals and needs the same three fixes.
   */
  const [pricingRefusal, setPricingRefusal] = useState<PricingRefusal | null>(null);
  const [attaching, setAttaching] = useState(false);
  const navigate = useNavigate();

  const visit = useVisitPicker(open);
  const { occupancy, reload: reloadOccupancy } = useKidsOccupancy(resource?.id ?? null, open);
  const queue = useWaitingList(resource?.id ?? null, open);

  const detail = useApi<ResourceRow>(open && resource ? `/resources/${resource.id}` : null);
  // The rate card's rules, not the resource's: `GET /resources/:id` embeds the
  // card a resource charges from, and a room attached to none has no rates at
  // all — which is why a check-in on it is refused before the child sits down.
  const rules = detail.data?.pricingList?.rules ?? [];

  useEffect(() => {
    if (!open) return;
    setMode('find');
    setQuery({ name: '', tagCode: '', guardianPhone: '' });
    setResults(null);
    setChild(null);
    setChildName('');
    setNewTagCode('');
    setGuardians([{ ...emptyGuardian, isPrimary: true }]);
    setPlannedMinutes('60');
    setPricingRuleId('');
    setPricingRefusal(null);
    visit.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resource?.id]);

  const full = (occupancy?.free ?? 1) <= 0;
  const minutes = Number(plannedMinutes);
  const plannedForApi = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : undefined;

  const searchable =
    query.name.trim().length > 0 ||
    query.tagCode.trim().length > 0 ||
    query.guardianPhone.trim().length > 0;

  const search = async () => {
    if (!searchable) return;
    setSearching(true);
    try {
      setResults(await searchChildren(query));
    } catch (error) {
      toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  /**
   * The registration half is refused before it is sent when it would leave a
   * child with nobody to call — the same rule `CreateChildDto` enforces, reached
   * before a half-registered child is written rather than after.
   */
  const newIsComplete =
    childName.trim().length > 0 && guardians.length > 0 && guardians.every(guardianIsComplete);
  const ready = mode === 'find' ? !!child : newIsComplete;

  const submit = async () => {
    if (!resource || !ready) return;
    setBusy(true);
    try {
      const { data } = await api.post<KidsCheckInResult>('/kids-area/check-in', {
        resourceId: resource.id,
        ...(mode === 'find'
          ? { childId: child!.id }
          : {
              childName: childName.trim(),
              ...(newTagCode.trim() ? { tagCode: newTagCode.trim() } : {}),
              guardians: guardians.map(guardianBody),
            }),
        // Where the time is billed. A kids area often has neither — the guardian
        // dropped the child off and left — and the server is happy with that.
        ...(visit.tableId ? { tableId: visit.tableId } : {}),
        ...(visit.checkId ? { checkId: visit.checkId } : {}),
        ...(plannedForApi ? { plannedMinutes: plannedForApi } : {}),
        ...(pricingRuleId ? { pricingRuleId } : {}),
        joinWaitingListIfFull: true,
      });

      if (data?.checkedIn) {
        toast.success(t('spaces.kids.checkedIn', { name: resource.name }));
      } else {
        toast.info(t('spaces.kids.queued', { position: data?.waitingListTicket?.position ?? 0 }));
      }
      onDone();
      onClose();
    } catch (error) {
      const failure = refusalOf(error);
      // The pricing refusals stay on the dialog with the way out; everything
      // else is a toast, and either way the sentence is this client's own.
      if (isPricingRefusal(failure)) setPricingRefusal(failure);
      else toast.error(t('spaces.resources.actionFailed'));
      void reloadOccupancy();
      void queue.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.kids.checkInTitle', { name: resource?.name ?? '' })}
      description={t('spaces.kids.checkInHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !ready}>
            {full ? t('spaces.kids.joinQueue') : t('spaces.resources.checkIn')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {pricingRefusal && (
          <RefusalNotice
            refusal={pricingRefusal}
            names={{ resource: resource?.name }}
            onFix={(fix) => {
              if (fix.kind === 'attachList') {
                setAttaching(true);
                return;
              }
              onClose();
              navigate(pricingListRoute(fix.listId));
            }}
          />
        )}

        {occupancy && (
          <div
            className={
              full
                ? 'flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm'
                : 'flex items-start gap-2 rounded-lg bg-[var(--muted)] p-3 text-sm'
            }
          >
            {full ? (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
            ) : (
              <Users className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
            )}
            <div>
              <p className={full ? 'font-medium text-amber-800 dark:text-amber-300' : 'font-medium'}>
                {t('spaces.resources.occupancy', {
                  used: occupancy.occupied,
                  capacity: occupancy.capacity,
                })}
              </p>
              <p className="text-xs text-ink-muted">
                {full
                  ? t('spaces.kids.fullHint')
                  : t('spaces.kids.freePlaces', { count: occupancy.free })}
                {occupancy.waitingCount > 0 &&
                  ` · ${t('spaces.kids.waitingCount', { count: occupancy.waitingCount })}`}
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'find' ? 'default' : 'outline'}
            onClick={() => setMode('find')}
          >
            <Search className="h-4 w-4" />
            {t('spaces.kids.findChild')}
          </Button>
          <Button
            size="sm"
            variant={mode === 'new' ? 'default' : 'outline'}
            onClick={() => setMode('new')}
          >
            <UserPlus className="h-4 w-4" />
            {t('spaces.kids.newChild')}
          </Button>
        </div>

        {mode === 'find' ? (
          <div className="space-y-3">
            <p className="text-xs text-ink-muted">{t('spaces.kids.findHint')}</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <Field label={t('spaces.kids.tagCode')}>
                <div className="relative">
                  <ScanLine className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-ink-muted" />
                  <Input
                    value={query.tagCode}
                    dir="ltr"
                    className="ps-9"
                    onChange={(event) =>
                      setQuery({ ...query, tagCode: event.currentTarget.value })
                    }
                    placeholder={t('spaces.kids.tagCodePlaceholder')}
                  />
                </div>
              </Field>
              <Field label={t('spaces.kids.childName')}>
                <Input
                  value={query.name}
                  onChange={(event) => setQuery({ ...query, name: event.currentTarget.value })}
                  placeholder={t('spaces.kids.childNamePlaceholder')}
                />
              </Field>
              <Field label={t('spaces.kids.guardianPhone')}>
                <div className="relative">
                  <Phone className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-ink-muted" />
                  <Input
                    type="tel"
                    dir="ltr"
                    className="ps-9"
                    value={query.guardianPhone}
                    onChange={(event) =>
                      setQuery({ ...query, guardianPhone: event.currentTarget.value })
                    }
                    placeholder="+9665…"
                  />
                </div>
              </Field>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={!searchable || searching}
              onClick={() => void search()}
            >
              <Search className="h-4 w-4" />
              {t('common.search')}
            </Button>

            {results !== null && results.length === 0 && (
              <p className="text-sm text-ink-muted">{t('spaces.kids.noMatch')}</p>
            )}

            {/*
              The chosen child's record, editable here. A second parent who is
              not on it will need a manager's signature to collect at six
              o'clock; the moment to add him is now, while the parent doing the
              checking-in is standing at the desk.
            */}
            {child && (
              <ChildGuardians
                child={child}
                canManage={can('children.manage')}
                onChanged={(updated) => {
                  setChild(updated);
                  setResults(
                    (rows) => rows?.map((row) => (row.id === updated.id ? updated : row)) ?? null,
                  );
                }}
              />
            )}

            {results !== null && results.length > 0 && (
              <ul className="divide-y rounded-lg border">
                {results.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => setChild(row)}
                      aria-pressed={child?.id === row.id}
                      className={`flex w-full flex-col items-start gap-0.5 p-3 text-start text-sm ${
                        child?.id === row.id ? 'bg-[var(--muted)]' : 'hover:bg-hover'
                      }`}
                    >
                      <span className="font-medium">
                        {row.displayName}
                        {row.tagCode && (
                          <span className="ms-2 text-xs text-ink-muted" dir="ltr">
                            {row.tagCode}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-ink-muted">
                        {row.guardians.length
                          ? row.guardians
                              .map((link) => `${link.guardian.name} · ${link.guardian.phone}`)
                              .join(' · ')
                          : t('spaces.kids.noGuardian')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('spaces.kids.childName')} required>
                <Input
                  value={childName}
                  onChange={(event) => setChildName(event.currentTarget.value)}
                  placeholder={t('spaces.kids.childNamePlaceholder')}
                />
              </Field>
              <Field label={t('spaces.kids.tagCode')} hint={t('spaces.kids.tagCodeHint')}>
                <div className="relative">
                  <ScanLine className="pointer-events-none absolute start-3 top-3.5 h-4 w-4 text-ink-muted" />
                  <Input
                    value={newTagCode}
                    dir="ltr"
                    className="ps-9"
                    onChange={(event) => setNewTagCode(event.currentTarget.value)}
                    placeholder={t('spaces.kids.tagCodePlaceholder')}
                  />
                </div>
              </Field>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium">{t('spaces.kids.guardians')}</p>
              <p className="mb-2 text-xs text-ink-muted">{t('spaces.kids.guardiansHint')}</p>
              <GuardianFields guardians={guardians} onChange={setGuardians} />
            </div>
          </div>
        )}

        {/*
          Where the time is billed. A kids area usually has neither a table nor a
          bill — the guardian dropped the child off and left — so this is
          optional and stays optional; naming a visit is what makes the play time
          join that table's bill instead of standing alone.
        */}
        <VisitPicker
          visits={visit.visits}
          sessionId={visit.sessionId}
          onChange={visit.setSessionId}
          check={visit.check}
          loading={visit.loading}
          hint={t('spaces.kids.visitHint')}
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('spaces.resources.plannedMinutes')}
            hint={t('spaces.resources.plannedHint')}
          >
            <Input
              type="number"
              min={0}
              value={plannedMinutes}
              onChange={(event) => setPlannedMinutes(event.currentTarget.value)}
            />
          </Field>
          <Field label={t('spaces.resources.pricingRule')}>
            <Select
              value={pricingRuleId}
              onChange={(event) => setPricingRuleId(event.currentTarget.value)}
            >
              <option value="">{t('spaces.resources.autoRule')}</option>
              {rules
                .filter((rule) => rule.isActive)
                .map((rule) => (
                  <option key={rule.id} value={rule.id}>
                    {rule.name}
                  </option>
                ))}
            </Select>
          </Field>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium">{t('spaces.resources.pricing')}</p>
          <PricingRuleList rules={rules} currency={currency} activeRuleId={pricingRuleId || null} />
        </div>

        {(full || queue.tickets.length > 0) && resource && (
          <div className="space-y-2">
            <p className="text-xs font-medium">{t('spaces.kids.queueTitle')}</p>
            <KidsWaitingList
              resourceId={resource.id}
              tickets={queue.tickets}
              loading={queue.loading}
              free={occupancy?.free ?? 0}
              onChanged={() => {
                queue.reload();
                void reloadOccupancy();
                onDone();
              }}
            />
          </div>
        )}
      </div>

      {/* Opened over the check-in rather than instead of it: the attendant came
          here to seat a child, and `pricingListId` is a field on the room. Once
          a card is attached they press Check in without finding their way back. */}
      {attaching && (
        <ResourceFormDialog
          open
          record={resource}
          onClose={() => setAttaching(false)}
          onSaved={() => setPricingRefusal(null)}
        />
      )}
    </Dialog>
  );
}

/**
 * §11 — check out by wristband or by child. Posts to `/kids-area/check-out`,
 * which settles package minutes before billing the remainder, then posts the
 * TIME charge exactly as a generic stop would.
 *
 * S-81 — and asks the question the desk was never asked: *who is collecting
 * this child?* Every guardian on the child's record may take them with nothing
 * to sign; anyone else is refused by the server, so the override below is a real
 * second signature, not a client-side courtesy.
 *
 * The plural is the one change of substance in the move off Party. The old model
 * had room for exactly one guardian, so the second parent needed a manager to
 * collect their own child; `ChildGuardian` is many-to-many precisely because a
 * child usually has two. What has not changed is that a person who is not on the
 * record cannot take the child without a manager's PIN and a written reason.
 */
export function KidsCheckOutDialog({
  open,
  resource,
  sessions,
  session,
  currency,
  onClose,
  onDone,
}: KidsDialogProps & { sessions: SessionRow[]; session: SessionRow | null }) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<'child' | 'tag'>('child');
  const [sessionId, setSessionId] = useState('');
  const [tagCode, setTagCode] = useState('');
  const [collectedById, setCollectedById] = useState('');
  const [collectedByName, setCollectedByName] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  /** The someone-else lookup: a guardian the venue holds, not on this record. */
  const [elsewherePhone, setElsewherePhone] = useState('');
  const [elsewhere, setElsewhere] = useState<GuardianRow[] | null>(null);
  const [looking, setLooking] = useState(false);
  /** The server's own words when it refuses a handover, kept beside the override. */
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toastRefusal = useRefusalToast();

  const childIds = useMemo(
    () => (open ? sessions.map((row) => row.childId ?? '').filter(Boolean) : []),
    [open, sessions],
  );
  const children = useChildDirectory(childIds);

  useEffect(() => {
    if (!open) return;
    setMode(session ? 'child' : 'tag');
    setSessionId(session?.id ?? '');
    setTagCode('');
    setElsewherePhone('');
    setElsewhere(null);
  }, [open, session]);

  /**
   * A scanned band is matched against the children already on screen, so the
   * wristband path reaches the same picker as the by-name path instead of being
   * a blind post. An unrecognised code still falls through to the server, which
   * resolves the tag itself.
   */
  const scanned = tagCode.trim().toLowerCase();
  const chosen =
    (mode === 'tag'
      ? sessions.find(
          (row) =>
            !!scanned &&
            (children[row.childId ?? '']?.tagCode ?? '').toLowerCase() === scanned,
        )
      : sessions.find((row) => row.id === sessionId)) ?? null;

  const meter = chosen ? meterFor(chosen, new Date()) : null;
  const child = chosen ? (children[chosen.childId ?? ''] ?? null) : null;
  const onFile = child?.guardians ?? [];

  /**
   * A different child means a different guardian: never carry an override over.
   *
   * The preselection is the server's rule and not a convenience — a child with
   * ONE guardian on file has an unambiguous collector and the server accepts an
   * omitted one; a child with two does not, so the desk is made to say which of
   * them turned up rather than being handed a default that is a coin toss.
   */
  useEffect(() => {
    setCollectedById(unambiguousGuardianId(child));
    setCollectedByName('');
    setManagerPin('');
    setOverrideReason('');
    setRefusal(null);
    setElsewhere(null);
    setElsewherePhone('');
  }, [chosen?.id, child]);

  const lookupElsewhere = async () => {
    if (elsewherePhone.trim().length < 4) return;
    setLooking(true);
    try {
      const matches = await searchChildren({ guardianPhone: elsewherePhone });
      const seen = new Map<string, GuardianRow>();
      for (const row of matches) {
        for (const link of row.guardians) {
          if (link.guardian.phone.trim() === elsewherePhone.trim()) {
            seen.set(link.guardianId, link.guardian);
          }
        }
      }
      setElsewhere([...seen.values()]);
    } catch (error) {
      toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
      setElsewhere([]);
    } finally {
      setLooking(false);
    }
  };

  /**
   * The server's rule, mirrored so the desk sees it before it posts, not after.
   *
   * The verdict is a function in `../kidsDesk` rather than a knot of
   * `disabled={…}` expressions here, because it is the one thing on this screen
   * that has to be provably right and a boolean expression can only be checked
   * by clicking it. `kidsDesk.test.ts` asserts the rule; this reads it.
   *
   * `identified` is deliberately "a session is on the board", not "a row is
   * selected in the picker": a child checked out from another terminal while
   * this dialog was open leaves `sessionId` pointing at nothing, and the old
   * expression let that be pressed — posting a body with neither a session nor a
   * tag, which is a 400 handed to an attendant who cannot act on it.
   */
  const identified = mode === 'tag' ? tagCode.trim().length > 0 : !!chosen;
  const state = collectionState({
    identified,
    child: chosen ? { guardianIds: guardianIdsOf(child) } : null,
    collectorId: collectedById,
  });
  const gates = releaseGates(state, { managerPin, overrideReason });

  const submit = async (asOverride: boolean) => {
    if (!resource) return;
    setBusy(true);
    setRefusal(null);
    try {
      const { data } = await api.post<SessionRow & { billing?: { amount?: string | null } }>(
        '/kids-area/check-out',
        {
          resourceId: resource.id,
          ...(chosen ? { sessionId: chosen.id } : { tagCode: tagCode.trim() }),
          ...(collectedById ? { collectedByGuardianId: collectedById } : {}),
          ...(asOverride
            ? {
                managerPin: managerPin.trim(),
                overrideReason: overrideReason.trim(),
                ...(collectedByName.trim() ? { collectedByName: collectedByName.trim() } : {}),
              }
            : {}),
        },
      );
      const amount = data?.billing?.amount;
      toast.success(
        amount
          ? t('spaces.kids.checkedOutWithAmount', {
              amount: formatCurrency(amount, currency, i18n.language),
            })
          : t('spaces.kids.checkedOut'),
      );
      onDone();
      onClose();
    } catch (error) {
      // A 403 here is the handover being refused, which is an instruction to the
      // desk rather than a failure — it belongs beside the override, not in a
      // toast that disappears while the manager is still walking over. It is
      // still the server's own sentence, which is the one place this sweep
      // stopped: the guardian rules it explains are not pricing, and a localized
      // vocabulary for them is a change of its own.
      if (statusOf(error) === 403) {
        setRefusal(apiMessage(error, t('spaces.resources.actionFailed')));
        return;
      }
      // Everything else, including the pricing gate. Checking a child out STOPS
      // a timed session, so `priceSession` runs and a room whose rate card was
      // retired underneath it is refused here exactly as a console's stop is —
      // in this client's own words, never in the API's English.
      toastRefusal(error, { resource: resource?.name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.kids.checkOutTitle', { name: resource?.name ?? '' })}
      description={t('spaces.resources.stopConfirm')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => void submit(false)}
            disabled={busy || !gates.release}
          >
            {t('spaces.resources.checkOut')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'tag' ? 'default' : 'outline'}
            onClick={() => setMode('tag')}
          >
            <ScanLine className="h-4 w-4" />
            {t('spaces.kids.byWristband')}
          </Button>
          <Button
            size="sm"
            variant={mode === 'child' ? 'default' : 'outline'}
            onClick={() => setMode('child')}
          >
            {t('spaces.kids.byChild')}
          </Button>
        </div>

        {mode === 'tag' ? (
          <Field label={t('spaces.kids.tagCode')} hint={t('spaces.kids.tagCodeScanHint')}>
            <Input
              value={tagCode}
              dir="ltr"
              autoFocus
              onChange={(event) => setTagCode(event.currentTarget.value)}
              placeholder={t('spaces.kids.tagCodePlaceholder')}
            />
          </Field>
        ) : (
          <Field label={t('spaces.kids.child')} required>
            <Select value={sessionId} onChange={(event) => setSessionId(event.currentTarget.value)}>
              <option value="">{t('spaces.kids.selectChild')}</option>
              {sessions.map((row) => {
                const on = children[row.childId ?? ''] ?? null;
                return (
                  <option key={row.id} value={row.id}>
                    {on?.displayName ?? t('spaces.resources.guest')}
                    {on?.tagCode ? ` · ${on.tagCode}` : ''}
                  </option>
                );
              })}
            </Select>
          </Field>
        )}

        {chosen && (
          <Field
            label={t('spaces.kids.collectedBy')}
            required
            hint={
              onFile.length > 1
                ? t('spaces.kids.collectedByManyHint', { count: onFile.length })
                : onFile.length === 1
                  ? t('spaces.kids.collectedByHint', { name: onFile[0].guardian.name })
                  : t('spaces.kids.collectedByNoGuardianHint')
            }
          >
            <Select
              value={collectedById}
              onChange={(event) => setCollectedById(event.currentTarget.value)}
            >
              <option value="">{t('spaces.kids.selectCollector')}</option>
              {onFile.map((link) => (
                <option key={link.guardianId} value={link.guardianId}>
                  {link.guardian.name}
                  {link.relationship ? ` · ${link.relationship}` : ''}
                  {link.isPrimary ? ` · ${t('spaces.kids.callFirst')}` : ''}
                </option>
              ))}
              {(elsewhere ?? []).map((guardian) => (
                <option key={guardian.id} value={guardian.id}>
                  {guardian.name} · {t('spaces.kids.notOnFile')}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {/*
          Somebody the venue holds a record for who is not on THIS child's
          record — the grandmother who turned up unannounced. Looked up by phone
          number, because that is a guardian's identity and it is what she can
          be asked for; there is deliberately no browse-every-guardian list at a
          desk terminal.
        */}
        {chosen && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-xs font-medium">{t('spaces.kids.someoneElse')}</p>
            <p className="text-xs text-ink-muted">{t('spaces.kids.someoneElseHint')}</p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field label={t('spaces.kids.guardianPhone')}>
                  <Input
                    type="tel"
                    dir="ltr"
                    value={elsewherePhone}
                    onChange={(event) => setElsewherePhone(event.currentTarget.value)}
                    placeholder="+9665…"
                  />
                </Field>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={looking || elsewherePhone.trim().length < 4}
                onClick={() => void lookupElsewhere()}
              >
                <Search className="h-4 w-4" />
                {t('common.search')}
              </Button>
            </div>
            {elsewhere !== null && elsewhere.length === 0 && (
              <p className="text-xs text-ink-muted">{t('spaces.kids.noGuardianMatch')}</p>
            )}
          </div>
        )}

        {(gates.needsSignature || refusal) && (
          <div className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/40">
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              <div className="text-sm">
                <p className="font-medium text-red-800 dark:text-red-300">
                  {t('spaces.kids.overrideTitle')}
                </p>
                <p className="text-xs text-red-700 dark:text-red-400">
                  {refusal ?? t('spaces.kids.overrideHint')}
                </p>
              </div>
            </div>

            <Field label={t('spaces.kids.collectedByName')} hint={t('spaces.kids.collectedByNameHint')}>
              <Input
                value={collectedByName}
                onChange={(event) => setCollectedByName(event.currentTarget.value)}
                placeholder={t('spaces.kids.collectedByNamePlaceholder')}
              />
            </Field>

            <Field label={t('spaces.kids.overrideReason')} required>
              <Input
                value={overrideReason}
                onChange={(event) => setOverrideReason(event.currentTarget.value)}
                placeholder={t('spaces.kids.overrideReasonPlaceholder')}
              />
            </Field>

            <Field label={t('spaces.kids.managerPin')} required hint={t('spaces.kids.managerPinHint')}>
              <Input
                type="password"
                value={managerPin}
                dir="ltr"
                autoComplete="off"
                onChange={(event) => setManagerPin(event.currentTarget.value)}
                placeholder="••••"
              />
            </Field>

            <Button
              variant="danger"
              className="w-full"
              disabled={busy || !gates.override}
              onClick={() => void submit(true)}
            >
              <ShieldAlert className="h-4 w-4" />
              {t('spaces.kids.overrideConfirm')}
            </Button>
          </div>
        )}

        {meter && (
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-ink-muted">{t('resource.elapsed')}</dt>
              <dd className="tabular-nums" dir="ltr">
                {formatDuration(meter.seconds)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('spaces.resources.finalAmount')}</dt>
              <dd className="font-semibold">
                {meter.amount === null
                  ? t('spaces.resources.noPricing')
                  : formatCurrency(meter.amount, currency, i18n.language)}
              </dd>
            </div>
            {onFile.length > 0 && (
              <div className="col-span-2">
                <dt className="text-xs text-ink-muted">{t('spaces.kids.guardians')}</dt>
                <dd>
                  {onFile
                    .map((link) => `${link.guardian.name} · ${link.guardian.phone}`)
                    .join(' · ')}
                </dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </Dialog>
  );
}
